import { useEffect, useState } from 'react';

export type DeviceClass = 'high' | 'mid' | 'low';

interface NavigatorExtended extends Navigator {
  deviceMemory?: number;
  hardwareConcurrency: number;
  userAgentData?: { mobile?: boolean };
}

/**
 * Composite heuristic for which hero branch to render.
 *
 * - high: desktop with hover, ≥ 8 cores, ≥ 4 GB device memory (when reported)
 * - mid: anything between
 * - low: mobile + low cores OR low memory OR Safari private (fallback to poster)
 *
 * Cached in sessionStorage after first compute.
 */
export function useDeviceClass(): DeviceClass | null {
  const [cls, setCls] = useState<DeviceClass | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      setCls('low');
      return;
    }
    const cached = sessionStorage.getItem('bed_device_class');
    if (cached === 'high' || cached === 'mid' || cached === 'low') {
      setCls(cached);
      return;
    }

    const nav = navigator as NavigatorExtended;
    const cores = nav.hardwareConcurrency ?? 4;
    const memory = nav.deviceMemory ?? 4;
    const hasHover = window.matchMedia('(hover: hover)').matches;
    const isMobile = nav.userAgentData?.mobile ?? !hasHover;

    let score = 0;
    if (cores >= 8) score += 2;
    else if (cores >= 4) score += 1;

    if (memory >= 8) score += 2;
    else if (memory >= 4) score += 1;

    if (hasHover) score += 1;
    if (isMobile) score -= 1;

    const result: DeviceClass = score >= 4 ? 'high' : score >= 2 ? 'mid' : 'low';
    try {
      sessionStorage.setItem('bed_device_class', result);
    } catch {
      // ignore storage errors (private mode, etc.) — treat as low
    }
    setCls(result);
  }, []);

  return cls;
}
